import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Form, Spinner } from 'react-bootstrap';
import { formatDate, formatRelativeTime } from '../../utils/dateFormatter';
import { formatProgress } from '../../utils/numberFormatter';
import { getPlatformDisplayName } from '../../utils/platformUtils';
import { getOtaCategoryLabel } from '../../utils/otaReviewCategories';
import { useEditReply, useSendReply, useSkipReply, useConfirmReplyPublished } from '../../hooks/useGuestReviews';
import {
  REPLY_STATUS,
  REPLY_PROBLEM_TEXT,
  SKIP_REASONS,
  SKIP_REASON_LABELS,
  FAILURE_TEXT,
  TEXT
} from '../../config/guestReviewLabels';
// One category wording across the page — the complaint form's own (be/config/complaints.js CATEGORIES).
import { CATEGORY_LABELS } from '../../config/complaintLabels';
import { StatusChip, ChoiceButton, ErrorLine, daysLeftLabel, useRunner, textDir } from './shared';
import OpenComplaintButton from '../complaints/OpenComplaintButton';
import FollowUpBlock from './FollowUpBlock';

/**
 * One guest review and our reply to it — list 2 «الرد على التقييمات»
 * (tasks/post-checkout-review-and-replies.md Phase 3; L18, L21, L23, L24, L31, L46, P1, P4).
 *
 * One click to approve the AI draft by default — and exactly the text on screen is what the server is
 * asked to send (L31). «تعديل» opens the text inline (L46); «تخطٍّ» asks for a reason and a confirmation
 * (P4). The private note is shown to ops apart from the review and never inside the draft (L21). A draft
 * that fails a check shows why and cannot be sent until it is edited. Everything shown is the server's;
 * after any action the list is refetched.
 */

const SENDABLE = ['needs_approval', 'failed'];
// Not sent yet — may be written, edited or skipped. An unclear send may already be live, so it is not.
const UNSENT = ['drafting', 'needs_approval', 'failed'];
// Final, or sent and on its way — shown as one line.
const DONE = ['published', 'waiting_moderation', 'skipped', 'expired', 'cannot_reply', 'replied_elsewhere'];

/** One line per part the AI check found a complaint in: where, what kind, and its one-line summary. */
function complaintLines(triage) {
  if (!triage || triage.status !== 'done') return [];
  return [['public', TEXT.complaintPublic], ['private', TEXT.complaintPrivate]]
    .filter(([part]) => triage[part] && triage[part].complaint === true)
    .map(([part, label]) => `${label} (${CATEGORY_LABELS[triage[part].category] || triage[part].category})`);
}

const ReplyCard = ({ reply, switches }) => {
  const writingByHand = reply.status === 'drafting' && !switches.replyDraft;
  const [editing, setEditing] = useState(reply.noDraft || writingByHand);
  const [text, setText] = useState(reply.text || '');
  const [skipping, setSkipping] = useState(false);
  const [skipReason, setSkipReason] = useState(null);
  const [skipNote, setSkipNote] = useState('');
  const edit = useEditReply();
  const send = useSendReply();
  const skip = useSkipReply();
  const confirmPublished = useConfirmReplyPublished();
  const { error, run } = useRunner();

  const busy = edit.isPending || send.isPending || skip.isPending || confirmPublished.isPending || reply.status === 'sending';
  const unsent = UNSENT.includes(reply.status);
  const blocked = reply.problems.length > 0;
  const canSend = switches.replySend && reply.platformOn && !blocked && !reply.windowClosing && SENDABLE.includes(reply.status);
  const days = daysLeftLabel(reply.daysLeft);
  const { lowScore, privateNote, urgent } = reply.attention;
  const complaints = complaintLines(reply.triage);
  const attentionWhy = [...complaints, lowScore && TEXT.attentionLowScore, privateNote && TEXT.attentionPrivateNote].filter(Boolean);
  const triageWaiting = switches.triage && reply.triage && reply.triage.status === 'pending'
    && Boolean(reply.review.text || reply.review.privateNote);
  const categories = reply.review.categoryScores.filter(c => typeof c.score === 'number');
  const platformName = getPlatformDisplayName(reply.platform);
  // Nothing left to do on the public reply — it shrinks to one line, so the card gets shorter as work is done.
  const doneReply = DONE.includes(reply.status) || (reply.status === 'sent' && !reply.notPublishedSince);

  const startEdit = () => { setText(reply.text || ''); setEditing(true); };
  const save = () => run(edit, { id: reply.id, text }, () => setEditing(false));
  // The text on screen travels with the approval: if it changed meanwhile, the server refuses (L31).
  const approve = (confirmUnknown = false) => run(send, { id: reply.id, text: reply.text, confirmUnknown });
  const confirmSkip = () => run(skip, { id: reply.id, reason: skipReason, note: skipNote }, () => setSkipping(false));
  const confirmOnPlatform = () => run(confirmPublished, { id: reply.id });

  return (
    <article className="gr-card">
      <header className="gr-card-head">
        <div className="gr-card-title">
          <span className="gr-unit">{reply.unit && reply.unit.name}</span>
          {reply.unit && reply.unit.code && <span className="gr-code" dir="ltr">{reply.unit.code}</span>}
          {/* The unit's page on this platform, when one is saved — to see the reply where guests will. */}
          {reply.listingUrl ? (
            <a
              className="gr-platform gr-platform--link"
              href={reply.listingUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={`${TEXT.openListing} ${platformName}`}
              aria-label={`${TEXT.openListing} ${platformName}`}
            >
              {platformName} <i className="bi bi-box-arrow-up-left" aria-hidden="true"></i>
            </a>
          ) : (
            <span className="gr-platform">{platformName}</span>
          )}
        </div>
        {days && <span className={`gr-days ${reply.daysLeft <= 2 ? 'gr-days--soon' : ''}`}>{days}</span>}
      </header>

      <div className="gr-details">
        {reply.guestName && <span dir="auto">{reply.guestName}</span>}
        {reply.receivedAt && <span>{TEXT.received} {formatDate(reply.receivedAt)}</span>}
        {reply.review.score !== null && <span>{TEXT.score} {formatProgress(reply.review.score, 10)}</span>}
      </div>
      {categories.length > 0 && (
        <div className="gr-categories">
          {categories.map(c => (
            <span key={c.category} className="gr-category">{getOtaCategoryLabel(c.category)} {formatProgress(c.score, 10)}</span>
          ))}
        </div>
      )}
      {/* «يحتاج انتباه» only when there is something to look at (review-complaint-triage T7); red when urgent. */}
      {/* «يحتاج انتباه» only when there is something to look at (review-complaint-triage T7); red when urgent.
          The complaint action sits inside the strip, next to its reason (L24 → tasks/complaints-to-fixes.md C1) —
          only when the review's stay is known; a complaint always sits on a booking. */}
      {(attentionWhy.length > 0 || reply.complaint) && (
        <div className={`gr-attention gr-attention--strip ${urgent ? 'gr-attention--urgent' : ''}`} role={urgent ? 'alert' : undefined}>
          <div className="gr-attention-body">
            {attentionWhy.length > 0 && (
              <div>
                {urgent && <strong>{TEXT.attentionUrgent} — </strong>}
                {TEXT.attention}: {attentionWhy.join('، ')}
              </div>
            )}
            {reply.triage && reply.triage.summaryAr && <div className="gr-attention-summary" dir="auto">{reply.triage.summaryAr}</div>}
          </div>
          {reply.complaint ? (
            <Link to={`/ops/reviews?list=complaints&id=${reply.complaint.id}`} className="gr-link gr-attention-action">
              {reply.complaint.status === 'open' ? TEXT.complaintOpen : TEXT.complaintClosed}
            </Link>
          ) : reply.hasBooking && (
            <span className="gr-attention-action"><OpenComplaintButton source={{ kind: 'review', id: reply.id }} variant="link" /></span>
          )}
        </div>
      )}
      {triageWaiting && <div className="gr-hint"><Spinner animation="border" size="sm" /> {TEXT.triagePending}</div>}

      {/* The guest's own words — public — and, apart from them, the private note (ops only, L21). */}
      <section className="gr-block" aria-label={TEXT.guestSaid}>
        <h3 className="gr-block-title">{TEXT.guestSaid}</h3>
        <blockquote className="gr-text" dir="auto">{reply.review.text}</blockquote>
        {reply.review.textAr && (
          <p className="gr-translation"><span className="gr-translation-label">{TEXT.translation}:</span> {reply.review.textAr}</p>
        )}
        {reply.review.privateNote && (
          <div className="gr-private-note">
            <div className="gr-private-note-head">
              <span className="gr-private-note-title">
                <i className="bi bi-lock-fill" aria-hidden="true"></i> {TEXT.privateNote}
              </span>
              <span className="gr-private-note-badge">{TEXT.privateNoteBadge}</span>
            </div>
            <p className="gr-private-note-text" dir="auto">{reply.review.privateNote}</p>
            <div className="gr-private-note-hint">{TEXT.privateNoteHint}</div>
          </div>
        )}
      </section>

      {/* What we send — each item says where it goes, so the two sends are never mistaken for each other. */}
      <div className="gr-outbox">
      <h3 className="gr-outbox-title">{TEXT.weSend}</h3>
      <section className={`gr-out ${doneReply ? 'gr-out--done' : ''}`} aria-label={TEXT.publicReplyTitle}>
        <div className="gr-block-head">
          <div>
            <h4 className="gr-out-title"><i className="bi bi-globe2" aria-hidden="true"></i> {TEXT.publicReplyTitle}</h4>
            <div className="gr-out-where">{TEXT.publicReplyWhere} {platformName}</div>
          </div>
          <StatusChip status={reply.status} labels={REPLY_STATUS} />
        </div>
        {doneReply ? (
          reply.text && <p className="gr-done-line" dir="auto">{reply.text}</p>
        ) : (<>

        {reply.status === 'drafting' && !editing && (
          <div className="gr-hint">
            {switches.replyDraft ? <><Spinner animation="border" size="sm" /> {TEXT.draftPending}</> : TEXT.replyDraftOff}
          </div>
        )}

        {editing ? (
          <div className="gr-edit">
            <Form.Control
              as="textarea"
              rows={4}
              className="touch-input gr-textarea"
              value={text}
              maxLength={1000}
              disabled={busy}
              onChange={e => setText(e.target.value)}
              placeholder={TEXT.replyPlaceholder}
              aria-label={TEXT.replyPlaceholder}
              dir={textDir(text)}
            />
            <ErrorLine message={error} />
            <div className="gr-actions">
              <Button className="touch-btn gr-primary" onClick={save} disabled={busy || !text.trim()}>
                {edit.isPending ? <Spinner animation="border" size="sm" /> : TEXT.save}
              </Button>
              <Button variant="light" className="touch-btn" onClick={() => { setText(reply.text || ''); setEditing(false); }} disabled={busy}>
                {TEXT.cancel}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {reply.text && <blockquote className="gr-text" dir="auto">{reply.text}</blockquote>}
            {reply.textAr && (
              <p className="gr-translation"><span className="gr-translation-label">{TEXT.translation}:</span> {reply.textAr}</p>
            )}
            {reply.noDraft && <div className="gr-attention">{TEXT.noDraft}</div>}
            {blocked && reply.text && (
              <div className="gr-problems" role="alert">
                <p className="gr-problems-title">{TEXT.problemsTitle}</p>
                <ul>
                  {reply.problems.map(p => <li key={p}>{REPLY_PROBLEM_TEXT[p] || p}</li>)}
                </ul>
              </div>
            )}
            <ErrorLine message={error} />

            {unsent && (
              <div className="gr-actions">
                {SENDABLE.includes(reply.status) && (
                  <Button className="touch-btn gr-primary" onClick={() => approve()} disabled={busy || !canSend}>
                    {send.isPending ? <Spinner animation="border" size="sm" /> : TEXT.approveReply}
                  </Button>
                )}
                <Button variant="outline-secondary" className="touch-btn" onClick={startEdit} disabled={busy}>
                  {reply.text ? TEXT.edit : TEXT.writeReply}
                </Button>
                <Button variant="link" className="touch-btn gr-link" onClick={() => setSkipping(true)} disabled={busy || skipping}>
                  {TEXT.skip}
                </Button>
              </div>
            )}

            {/* An unclear send may already be live — resend only after checking, or confirm it is there. */}
            {reply.status === 'unknown' && (
              <div className="gr-actions">
                <Button
                  className="touch-btn gr-primary"
                  onClick={() => approve(true)}
                  disabled={busy || !switches.replySend || !reply.platformOn || blocked || reply.windowClosing}
                >
                  {TEXT.resendChecked}
                </Button>
                <Button variant="outline-secondary" className="touch-btn" onClick={confirmOnPlatform} disabled={busy}>
                  {confirmPublished.isPending ? <Spinner animation="border" size="sm" /> : TEXT.confirmPublished}
                </Button>
              </div>
            )}
            {reply.status === 'sent' && reply.notPublishedSince && (
              <div className="gr-actions">
                <Button variant="outline-secondary" className="touch-btn" onClick={confirmOnPlatform} disabled={busy}>
                  {confirmPublished.isPending ? <Spinner animation="border" size="sm" /> : TEXT.confirmPublished}
                </Button>
              </div>
            )}
            {reply.windowClosing && (unsent || reply.status === 'unknown') && <div className="gr-hint">{TEXT.windowClosing}</div>}
          </>
        )}

        {/* Final — asked once more, with a reason (P4). */}
        {unsent && skipping && (
          <div className="gr-confirm" role="group" aria-label={TEXT.skip}>
            <p className="gr-confirm-text">{TEXT.skipConfirm}</p>
            <div className="gr-choices">
              {SKIP_REASONS.map(code => (
                <ChoiceButton key={code} active={skipReason === code} disabled={busy} onClick={() => setSkipReason(code)}>
                  {SKIP_REASON_LABELS[code]}
                </ChoiceButton>
              ))}
            </div>
            <Form.Control
              as="textarea"
              rows={2}
              className="touch-input gr-textarea mt-2"
              value={skipNote}
              maxLength={500}
              disabled={busy}
              onChange={e => setSkipNote(e.target.value)}
              placeholder={TEXT.skipNotePlaceholder}
              aria-label={TEXT.skipNotePlaceholder}
              dir={textDir(skipNote)}
            />
            <div className="gr-actions">
              <Button className="touch-btn gr-primary" onClick={confirmSkip} disabled={busy || !skipReason}>
                {skip.isPending ? <Spinner animation="border" size="sm" /> : TEXT.confirm}
              </Button>
              <Button variant="light" className="touch-btn" onClick={() => setSkipping(false)} disabled={busy}>
                {TEXT.cancel}
              </Button>
            </div>
          </div>
        )}

        {!reply.platformOn && <div className="gr-hint">{TEXT.platformOff}</div>}

        <div className="gr-details">
          {reply.status === 'sending' && <span><Spinner animation="border" size="sm" /> {REPLY_STATUS.sending}</span>}
          {reply.failure && ['failed', 'unknown'].includes(reply.status) && (
            <span className="gr-failure">{FAILURE_TEXT[reply.failure.code]}</span>
          )}
          {reply.notPublishedSince && (
            <span className="gr-failure">{TEXT.notPublished} ({formatRelativeTime(reply.notPublishedSince)})</span>
          )}
          {reply.approvedByName && ['sending', 'sent'].includes(reply.status) && (
            <span>{TEXT.by} {reply.approvedByName}</span>
          )}
          {reply.edited && <span>{TEXT.edited}</span>}
        </div>
        </>)}
      </section>

      {/* A private-note complaint gets a private message — its own approval, apart from the public reply. */}
      {reply.followUp && (
        <FollowUpBlock
          key={`${reply.id}-${reply.followUp.status}`}
          replyId={reply.id}
          followUp={reply.followUp}
          sendOn={switches.followUpSend}
        />
      )}
      </div>
    </article>
  );
};

export default ReplyCard;
